import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useLms } from '../../context/LmsContext';
import CoursePreview from '../../components/CoursePreview';
import { CourseGradingSheet } from '../../components/GradingSheets';
import { Card, DataTable, PageHeader, ProgressBar, StatusBadge } from '../../components/ui';
import { normalizeUserRoles } from '../../utils/navigation';
import { assignmentGrade, byId, derivedAssignmentStatus, fullName, getAssignmentProgress, getCourse, getExercisesForVersion, getLessonsForVersion, getModulesForVersion, getQuizzesForVersion, trainerNames } from '../../utils/lmsSelectors';

function PreviewModal({ course, data, onClose }) {
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <div className="builder-modal wide-modal" role="dialog" aria-modal="true" aria-label="Course learner preview" onMouseDown={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <div><h2>Preview as learner</h2><p className="muted small">This is the same learner-style structure without recording progress.</p></div>
          <button className="button button-ghost" type="button" onClick={onClose}>Close</button>
        </div>
        <CoursePreview data={data} course={course} mode="preview" />
      </div>
    </div>
  );
}

export default function AdminCourseDetail() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { data, currentUser, publishCourse, requestCourseChanges, archiveCourse, assignCourseTrainers, deleteCourse } = useLms();
  const course = getCourse(data, courseId);
  const [trainerSearch, setTrainerSearch] = useState('');
  const [trainerIds, setTrainerIds] = useState(course?.trainerIds || []);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [trainerMessage, setTrainerMessage] = useState('');
  const [courseMessage, setCourseMessage] = useState('');
  if (!course) return <Card>Course not found.</Card>;
  const version = byId(data.courseVersions, course.currentVersionId);
  const modules = getModulesForVersion(data, course.currentVersionId);
  const lessons = getLessonsForVersion(data, course.currentVersionId);
  const quizzes = getQuizzesForVersion(data, course.currentVersionId);
  const exercises = getExercisesForVersion(data, course.currentVersionId);
  const assignments = data.assignments.filter((assignment) => assignment.courseId === course.id);
  const trainerOptions = data.users
    .filter((user) => (normalizeUserRoles(user).includes('trainer') || normalizeUserRoles(user).includes('admin')) && user.status === 'active')
    .filter((user) => !trainerSearch || `${fullName(user)} ${user.email} ${user.team || ''}`.toLowerCase().includes(trainerSearch.trim().toLowerCase()));

  const toggleTrainer = (trainerId) => {
    setTrainerIds((current) => current.includes(trainerId) ? current.filter((id) => id !== trainerId) : [...current, trainerId]);
  };

  const saveTrainers = () => {
    const result = assignCourseTrainers(course.id, trainerIds, currentUser.id);
    setTrainerMessage(result.ok ? 'Course trainers updated.' : result.message);
  };

  const handleDeleteCourse = () => {
    const activeAssignments = assignments.filter((assignment) => assignment.status !== 'revoked').length;
    const revokedAssignments = assignments.filter((assignment) => assignment.status === 'revoked').length;
    const details = activeAssignments
      ? `\n\nThis course has ${activeAssignments} active/completed learner record(s). Revoke those assignments first before deleting the course.`
      : revokedAssignments
        ? `\n\nThis will also remove ${revokedAssignments} revoked learner assignment record(s) tied to this course.`
        : '';
    if (!window.confirm(`Delete course "${course.title}"? This cannot be undone.${details}`)) return;
    const result = deleteCourse(course.id, currentUser.id);
    if (result.ok) {
      navigate('/admin/courses');
      return;
    }
    setCourseMessage(result.message);
  };

  const rows = assignments.map((assignment) => {
    const learner = byId(data.users, assignment.assignedToUserId);
    const progress = getAssignmentProgress(data, assignment);
    return { id: assignment.id, learner: fullName(learner), status: derivedAssignmentStatus(data, assignment), progress: progress.percent, grade: assignmentGrade(data, assignment), dueDate: assignment.dueDate };
  });

  return (
    <>
      <PageHeader
        title={course.title}
        eyebrow="Admin Course Detail"
        description={course.description}
        action={<div className="button-row compact"><button className="button button-secondary" type="button" onClick={() => setPreviewOpen(true)}>Preview as learner</button><Link className="button button-secondary" to={`/admin/courses/${course.id}/builder`}>Open builder</Link><button className="button button-danger" type="button" onClick={handleDeleteCourse}>Delete course</button></div>}
      />
      {courseMessage && <Card><p className="feedback-box compact-feedback">{courseMessage}</p></Card>}
      <div className="grid four">
        <Card><span className="muted">Status</span><h2><StatusBadge status={course.status} /></h2></Card>
        <Card><span className="muted">Version</span><h2>v{version?.versionNumber}</h2><p className="muted small">{version?.changeSummary}</p></Card>
        <Card><span className="muted">Structure</span><h2>{modules.length} modules</h2><p className="muted small">{lessons.length} lessons • {quizzes.length} quizzes • {exercises.length} exercises</p></Card>
        <Card><span className="muted">Trainer/s</span><h2 className="small-heading-wrap">{trainerNames(data, course)}</h2></Card>
      </div>
      <div className="grid two uneven">
        <Card>
          <h2>Governance actions</h2>
          <div className="button-row">
            <button className="button button-primary" disabled={course.status === 'published'} onClick={() => publishCourse(course.id, currentUser.id)}>Publish course</button>
            <button className="button button-secondary" onClick={() => requestCourseChanges(course.id, currentUser.id)}>Request changes</button>
            <button className="button button-danger" onClick={() => archiveCourse(course.id, currentUser.id)}>Archive</button>
          </div>
        </Card>
        <Card>
          <h2>Assign multiple trainers</h2>
          <div className="form-stack">
            <input className="search-input" placeholder="Search trainer by name, email, or team" value={trainerSearch} onChange={(e) => setTrainerSearch(e.target.value)} />
            <div className="checkbox-grid scroll-box">
              {trainerOptions.map((trainer) => (
                <label key={trainer.id} className="check-row"><input type="checkbox" checked={trainerIds.includes(trainer.id)} onChange={() => toggleTrainer(trainer.id)} /> {fullName(trainer)} <small className="muted">• {trainer.email}</small></label>
              ))}
            </div>
            <button className="button button-primary" type="button" onClick={saveTrainers}>Save trainers</button>
            {trainerMessage && <p className="feedback-box compact-feedback">{trainerMessage}</p>}
          </div>
        </Card>
      </div>
      <Card>
        <h2>Course contents</h2>
        {modules.map((module) => (
          <div key={module.id} className="module-block">
            <h3>{module.title}</h3>
            <p className="muted">{module.description}</p>
            <ul>
              {lessons.filter((lesson) => lesson.moduleId === module.id).map((lesson) => <li key={lesson.id}>{lesson.title} <span className="muted small">({lesson.lessonType}, {lesson.estimatedMinutes} min)</span></li>)}
              {quizzes.filter((quiz) => quiz.moduleId === module.id).map((quiz) => <li key={quiz.id}>{quiz.title} <span className="muted small">(quiz, pass {quiz.passingScore}%)</span></li>)}
              {exercises.filter((exercise) => exercise.moduleId === module.id).map((exercise) => <li key={exercise.id}>{exercise.title} <span className="muted small">(graded exercise)</span></li>)}
            </ul>
          </div>
        ))}
      </Card>
      <Card>
        <h2>Learner performance summary</h2>
        <DataTable
          rows={rows}
          columns={[
            { key: 'learner', label: 'Learner' },
            { key: 'status', label: 'Status', render: (row) => <StatusBadge status={row.status} /> },
            { key: 'progress', label: 'Progress', render: (row) => <ProgressBar value={row.progress} /> },
            { key: 'grade', label: 'Grade', render: (row) => row.grade === null ? '—' : `${row.grade}%` },
            { key: 'dueDate', label: 'Due date' }
          ]}
          emptyMessage="No learners assigned to this course yet."
        />
      </Card>
      <Card>
        <h2>Course grading sheet</h2>
        <p className="muted small">Shows every learner grade for every quiz and graded exercise in this course.</p>
        <CourseGradingSheet data={data} courseId={course.id} />
      </Card>
      {previewOpen && <PreviewModal course={course} data={data} onClose={() => setPreviewOpen(false)} />}
    </>
  );
}
